import React, { useState, useEffect } from 'react';
import { Card, Container, Row, Col, Table, Button, Modal, Form, Badge, Alert, InputGroup } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faUserShield, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../api/api';

const UserAdministrationPage = () => {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
    const [selectedUser, setSelectedUser] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [pagination, setPagination] = useState({
        totalPages: 0,
        totalElements: 0,
        last: false,
        first: true
    });
    const [filters, setFilters] = useState({
        username: '',
        codeExpert: ''
    });
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        code_expert: '',
        state: true,
        roleIds: []
    });

    // Fetch users and roles on component mount
    useEffect(() => {
        fetchUsers();
        fetchRoles();
    }, [currentPage]);

    // Fetch users when filters change (reset to page 0)
    const handleSearch = () => {
        setCurrentPage(0);
        fetchUsers();
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            params.append('page', currentPage);
            if (filters.username.trim()) params.append('username', filters.username.trim());
            if (filters.codeExpert.trim()) params.append('codeExpert', filters.codeExpert.trim());

            const response = await axiosInstance.get(`/api/user/all?${params.toString()}`);
            // Extract content array from paginated response
            const userData = response.data?.content || [];
            setUsers(Array.isArray(userData) ? userData : []);

            // Extract pagination metadata
            setPagination({
                totalPages: response.data?.totalPages || 0,
                totalElements: response.data?.totalElements || 0,
                last: response.data?.last || false,
                first: response.data?.first || true
            });

            setError(null);
        } catch (err) {
            setError('Error al cargar los usuarios: ' + err.message);
            setUsers([]); // Ensure users is always an array
        } finally {
            setLoading(false);
        }
    };

    const handleClearFilters = () => {
        setFilters({
            username: '',
            codeExpert: ''
        });
        setCurrentPage(0);
    };

    const fetchRoles = async () => {
        try {
            const response = await axiosInstance.get('/api/roles/all');
            setRoles(response.data);
        } catch (err) {
            console.error('Error al cargar los roles:', err);
        }
    };

    const handleShowCreateModal = () => {
        setModalMode('create');
        setSelectedUser(null);
        setFormData({
            username: '',
            password: '',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            address: '',
            code_expert: '',
            state: true,
            roleIds: []
        });
        setShowModal(true);
    };

    const handleShowEditModal = (user) => {
        setModalMode('edit');
        setSelectedUser(user);
        setFormData({
            username: user.username || '',
            password: '', // Don't populate password for security
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            phone: user.phone || '',
            address: user.address || '',
            code_expert: user.code_expert || '',
            state: user.state !== undefined ? user.state : true,
            roleIds: user.roles ? user.roles.map(role => role.id) : []
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedUser(null);
        setFormData({
            username: '',
            password: '',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            address: '',
            code_expert: '',
            state: true,
            roleIds: []
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleRoleChange = (roleId) => {
        setFormData(prev => {
            const roleIds = prev.roleIds || [];
            if (roleIds.includes(roleId)) {
                return { ...prev, roleIds: roleIds.filter(id => id !== roleId) };
            } else {
                return { ...prev, roleIds: [...roleIds, roleId] };
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (modalMode === 'create') {
                // Use the correct API endpoint for creating users
                await axiosInstance.post('/api/user/create', formData);
            } else {
                // For edit, only send password if it was changed
                const updateData = { ...formData };
                if (!updateData.password) {
                    delete updateData.password;
                }
                await axiosInstance.put(`/user/${selectedUser.id}`, updateData);
            }
            handleCloseModal();
            fetchUsers();
        } catch (err) {
            setError(`Error al ${modalMode === 'create' ? 'crear' : 'actualizar'} usuario: ` + err.message);
        }
    };

    const handleDelete = async (userId) => {
        if (window.confirm('¿Está seguro de que desea eliminar este usuario?')) {
            try {
                await axiosInstance.delete(`/user/${userId}`);
                fetchUsers();
            } catch (err) {
                setError('Error al eliminar usuario: ' + err.message);
            }
        }
    };

    const getRoleBadges = (roles) => {
        if (!roles || roles.length === 0) return <Badge bg="secondary">Sin rol</Badge>;
        return roles.map((role, index) => (
            <Badge key={index} bg="primary" className="me-1">
                {role.name}
            </Badge>
        ));
    };

    return (
        <Container fluid className="px-0">
            <Row>
                <Col xs={12} className="p-3">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h3 className="mb-0">
                                <FontAwesomeIcon icon={faUserShield} className="me-2" />
                                Administración de usuarios
                            </h3>
                            <Button variant="primary" onClick={handleShowCreateModal}>
                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                Crear Usuario
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

                            {/* Filters Section */}
                            <Row className="mb-4">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>Usuario</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Buscar por usuario..."
                                            value={filters.username}
                                            onChange={(e) => setFilters({ ...filters, username: e.target.value })}
                                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>Código Experto</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Buscar por código..."
                                            value={filters.codeExpert}
                                            onChange={(e) => setFilters({ ...filters, codeExpert: e.target.value })}
                                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4} className="d-flex align-items-end gap-2">
                                    <Button variant="primary" onClick={handleSearch}>
                                        <FontAwesomeIcon icon={faSearch} className="me-2" />
                                        Buscar
                                    </Button>
                                    <Button variant="secondary" onClick={handleClearFilters}>
                                        <FontAwesomeIcon icon={faTimes} className="me-2" />
                                        Limpiar
                                    </Button>
                                </Col>
                            </Row>

                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Cargando...</span>
                                    </div>
                                </div>
                            ) : (
                                <Table responsive hover>
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Usuario</th>
                                            <th>Nombre</th>
                                            <th>Email</th>
                                            <th>Teléfono</th>
                                            <th>Código Experto</th>
                                            <th>Roles</th>
                                            <th>Estado</th>
                                            <th>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.length === 0 ? (
                                            <tr>
                                                <td colSpan="9" className="text-center py-4">
                                                    No hay usuarios registrados
                                                </td>
                                            </tr>
                                        ) : (
                                            users.map((user) => (
                                                <tr key={user.id}>
                                                    <td>{user.id}</td>
                                                    <td>{user.username}</td>
                                                    <td>{`${user.firstName || ''} ${user.lastName || ''}`.trim() || '-'}</td>
                                                    <td>{user.email || '-'}</td>
                                                    <td>{user.phone || '-'}</td>
                                                    <td>{user.codeExpert || '-'}</td>
                                                    <td>{getRoleBadges(user.roles)}</td>
                                                    <td>
                                                        <Badge bg={user.state ? 'success' : 'danger'}>
                                                            {user.state ? 'Activo' : 'Inactivo'}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Button
                                                            variant="info"
                                                            size="sm"
                                                            className="me-2"
                                                            onClick={() => handleShowEditModal(user)}
                                                        >
                                                            <FontAwesomeIcon icon={faEdit} />
                                                        </Button>
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => handleDelete(user.id)}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            )}

                            {/* Pagination Controls */}
                            {!loading && users.length > 0 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <Button
                                        variant="outline-primary"
                                        disabled={pagination.first}
                                        onClick={() => setCurrentPage(currentPage - 1)}
                                    >
                                        Anterior
                                    </Button>
                                    <span className="text-muted">
                                        Página {currentPage + 1} de {pagination.totalPages} ({pagination.totalElements} usuarios)
                                    </span>
                                    <Button
                                        variant="outline-primary"
                                        onClick={() => setCurrentPage(currentPage + 1)}
                                        disabled={pagination.last}
                                    >
                                        Siguiente
                                    </Button>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Create/Edit Modal */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalMode === 'create' ? 'Crear Nuevo Usuario' : 'Editar Usuario'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Usuario *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="username"
                                        value={formData.username}
                                        onChange={handleInputChange}
                                        required
                                        disabled={modalMode === 'edit'}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Contraseña {modalMode === 'create' ? '*' : '(dejar vacío para no cambiar)'}
                                    </Form.Label>
                                    <Form.Control
                                        type="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        required={modalMode === 'create'}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nombre</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Apellido</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Teléfono</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Dirección</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Código Experto</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="code_expert"
                                        value={formData.code_expert}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Roles</Form.Label>
                                    <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                        {roles.length === 0 ? (
                                            <p className="text-muted mb-0">No hay roles disponibles</p>
                                        ) : (
                                            roles.map((role) => (
                                                <Form.Check
                                                    key={role.id}
                                                    type="checkbox"
                                                    id={`role-${role.id}`}
                                                    label={role.name}
                                                    checked={formData.roleIds?.includes(role.id) || false}
                                                    onChange={() => handleRoleChange(role.id)}
                                                    className="mb-2"
                                                />
                                            ))
                                        )}
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        name="state"
                                        label="Usuario Activo"
                                        checked={formData.state}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit">
                            {modalMode === 'create' ? 'Crear' : 'Actualizar'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
};

export default UserAdministrationPage;
